import type { EstimateItem, EstimateUnit, PricingMode } from './estimate.types';
import {
  calculatePercentageMoney,
  multiplyMoney,
  roundMoney,
  sumMoney,
} from './money';

export const MANUAL_ITEM_UNITS = ['m²', 'mb', 'szt.', 'pkt', 'kpl.', 'h', 'ryczałt'] as const;
export const MANUAL_ITEM_TYPES = ['labor', 'material', 'auxiliary'] as const;

export type ManualItemUnit = (typeof MANUAL_ITEM_UNITS)[number];
export type ManualItemType = (typeof MANUAL_ITEM_TYPES)[number];

export interface ManualItemInput {
  name: string;
  quantity: number;
  unit: ManualItemUnit;
  unitPrice: number;
  itemType: ManualItemType;
}

export type ManualEstimateItem = EstimateItem & {
  itemType: ManualItemType;
  source: 'manual';
};

export interface ApprovedOfferPricing {
  workNet: number;
  materialNet: number;
  netTotal: number;
  vatRate: number;
  vatAmount: number;
  grossTotal: number;
}

function formatNumber(value: number): string {
  return value.toLocaleString('pl-PL', { maximumFractionDigits: 2 });
}

function getPricingMode(unit: ManualItemUnit): PricingMode {
  if (unit === 'm²') return 'area';
  if (unit === 'mb') return 'linear';
  if (unit === 'h') return 'hourly';
  if (unit === 'ryczałt') return 'flat';
  return 'piece';
}

export function validateManualItemInput(input: ManualItemInput): string | null {
  if (!input.name.trim()) return 'Podaj nazwę pozycji.';
  if (!Number.isFinite(input.quantity) || input.quantity <= 0) return 'Ilość musi być większa od 0.';
  if (!Number.isFinite(input.unitPrice) || input.unitPrice < 0) return 'Cena jednostkowa nie może być ujemna.';
  return null;
}

export function calculateManualItemTotal(quantity: number, unitPrice: number): number {
  return multiplyMoney(quantity, unitPrice);
}

export function createManualEstimateItem(id: string, input: ManualItemInput): ManualEstimateItem {
  const validationMessage = validateManualItemInput(input);
  if (validationMessage) throw new Error(validationMessage);

  const name = input.name.trim();
  const unitPrice = roundMoney(input.unitPrice);
  const total = calculateManualItemTotal(input.quantity, unitPrice);
  return {
    id,
    label: name,
    category: `manual_${input.itemType}`,
    quantity: input.quantity,
    unit: input.unit as EstimateUnit,
    unitPrice,
    total,
    calculation: `${formatNumber(input.quantity)} ${input.unit} × ${formatNumber(input.unitPrice)} zł/${input.unit} = ${formatNumber(total)} zł`,
    pricingMode: getPricingMode(input.unit),
    unitRateNetto: unitPrice,
    totalNetto: total,
    itemType: input.itemType,
    source: 'manual',
  };
}

export function updateManualEstimateItem(item: ManualEstimateItem, input: ManualItemInput): ManualEstimateItem {
  return createManualEstimateItem(item.id, input);
}

export function removeManualEstimateItem(items: ManualEstimateItem[], id: string): ManualEstimateItem[] {
  return items.filter((item) => item.id !== id);
}

export function calculateApprovedOfferPricing(input: {
  items: EstimateItem[];
  baseMaterialNet: number;
  vatRate: number;
}): ApprovedOfferPricing {
  const manualMaterialNet = sumMoney(
    input.items
      .filter((item) => item.source === 'manual' && item.itemType === 'material')
      .map((item) => item.totalNetto)
  );
  const workNet = sumMoney(
    input.items
      .filter((item) => !(item.source === 'manual' && item.itemType === 'material'))
      .map((item) => item.totalNetto)
  );
  const materialNet = sumMoney([input.baseMaterialNet, manualMaterialNet]);
  const netTotal = sumMoney([workNet, materialNet]);
  const vatAmount = calculatePercentageMoney(netTotal, input.vatRate);

  return {
    workNet,
    materialNet,
    netTotal,
    vatRate: input.vatRate,
    vatAmount,
    grossTotal: sumMoney([netTotal, vatAmount]),
  };
}
